import React, { useCallback, useContext, useEffect } from "react";
import { useInView } from "react-intersection-observer";

import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop } from "lodash";

import { inVideocallMode } from "~/lib/utils";
import { useModals } from "~/ui";
import Button, { type ButtonProps } from "~/ui/button";

import OutOfCreditsModal from "../NoteHeader/OutOfCreditsModal";

import styles from "./SuggestedActions.module.css";

export type AbstractAction = {
	id: string;
	button: React.ElementType;
	menuItem: React.ElementType;
};

interface OverflowContextValue {
	rootRef: React.RefObject<HTMLDivElement>;
	addToMenu: (id: string) => void;
	removeFromMenu: (id: string) => void;
}

export const OverflowContext = React.createContext<OverflowContextValue>({
	rootRef: React.createRef(),
	addToMenu: noop,
	removeFromMenu: noop,
});
OverflowContext.displayName = "OverflowContext";

interface ActionButtonProps extends ButtonProps {
	actionId: string;
}

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
	({ children, className, actionId, ...props }, forwardRef) => {
		const { rootRef, addToMenu, removeFromMenu } = useContext(OverflowContext);
		const { ref, inView } = useInView({
			threshold: 1,
			root: rootRef.current,
		});

		useEffect(() => {
			if (inView) {
				removeFromMenu(actionId);
			} else {
				addToMenu(actionId);
			}
		}, [inView, actionId, addToMenu, removeFromMenu]);

		const mergedRef = useCallback(
			(element: HTMLButtonElement) => {
				[ref, forwardRef].forEach(loopRef => {
					if (typeof loopRef === "function") {
						loopRef(element);
					} else if (loopRef !== null) {
						loopRef.current = element;
					}
				});
			},
			[ref, forwardRef],
		);

		return (
			<Button
				ref={mergedRef}
				size="slim"
				variant="secondary"
				className={cx(styles.actionButton, className, { [styles.hidden]: !inView })}
				{...props}
			>
				{children}
			</Button>
		);
	},
);
ActionButton.displayName = "ActionButton";

export function CreditsCount({
	creditsRemaining,
	interactive,
	className,
}: {
	creditsRemaining: number;
	interactive?: boolean;
	className?: string;
}) {
	const modals = useModals();

	const onClick = useCallback(
		(e: React.MouseEvent<HTMLSpanElement>) => {
			if (interactive) return;
			e.stopPropagation();
			modals.open(({ close }) => <OutOfCreditsModal onClose={close} />);
		},
		[interactive, modals],
	);

	return (
		<span
			className={cx(styles.creditsCount, className, { [styles.interactive]: interactive })}
			onClick={onClick}
			data-test-id="suggested-actions-credits-count"
		>
			<FontAwesomeIcon icon={faArrowAltCircleUp} />
			{creditsRemaining !== 0 || inVideocallMode ? `${creditsRemaining} left` : "Out of credits"}
		</span>
	);
}
